import { guessPlatform, megabytes, useDesktopRelease } from './useDesktopRelease';

/**
 * "Get the desktop app", where it makes sense to say it.
 *
 * ## It renders nothing more often than it renders something
 *
 * Inside the desktop app itself, because software that advertises itself to
 * itself feels unmaintained. Before the first release is published, because
 * there is nothing to download. And on a platform nothing has been built for
 * yet - macOS and Linux, until somebody builds them - because a button that
 * hands a Mac user a `.exe` is worse than no button.
 *
 * All three are the same shape: ask what exists, offer what fits, otherwise say
 * nothing. `useDesktopRelease` returns null for the first two and the platform
 * lookup covers the third.
 *
 * ## Two variants rather than two components
 *
 * The menu row and the footer link are the same fact in two places - one for
 * people who are signed in and one for everybody, including the visitor who has
 * not made an account yet and is the most likely to want it. Splitting them
 * into separate files would be two things to remember to hide.
 */
export function DesktopDownload({ variant }: { variant: 'menu' | 'footer' }) {
  const release = useDesktopRelease();
  const platform = guessPlatform();
  const build = release && platform ? release.platforms[platform] : undefined;

  if (!release || !build) return null;

  const size = megabytes(build.size);

  /*
   * A plain anchor, and deliberately not a router `Link`: this leaves the app
   * for a file on another path, which the SPA router would try to handle as a
   * route and answer with the not-found page.
   *
   * `download` asks the browser to save rather than navigate, which is what
   * stops Chrome opening an 80MB binary in a tab. It only applies same-origin,
   * so `DESKTOP_DOWNLOAD_URL` has to name whichever domain the site is being
   * served from rather than the address baked into installed copies as their
   * update feed - both serve the same directory, and only one of them is
   * same-origin to a person reading this page.
   */
  if (variant === 'footer') {
    return (
      <a
        href={build.url}
        download
        className="meta text-soft transition-colors hover:text-ink"
      >
        Desktop app
      </a>
    );
  }

  /*
   * The separator belongs to this component, not to the menu around it.
   * Rendered by the caller it would leave a bordered empty strip in the menu
   * every time there is nothing to offer - which is most of the time, and
   * always in the desktop app.
   */
  return (
    <div className="border-t border-line py-1">
      <a
        href={build.url}
        download
        data-desktop-download
        className="flex items-center gap-3 px-4 py-2.5 text-sm text-soft transition-colors hover:bg-raised hover:text-ink"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate text-soft">
          <DownloadGlyph />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate">Get the {build.label} app</span>

          {/*
            The version and size, quietly. Somebody deciding whether to spend
            80MB deserves to know it is 80MB before the download starts, and
            somebody who already has it can tell at a glance whether this is
            newer than what they installed.
          */}
          <span className="block truncate text-xs text-faint">
            {release.version}
            {size ? ` · ${size}` : ''}
          </span>
        </span>
      </a>
    </div>
  );
}

function DownloadGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden
    >
      <path d="M8 2.5v8M4.5 7.5 8 11l3.5-3.5M2.5 13.5h11" />
    </svg>
  );
}
